import { ProgressSpinner } from "primereact/progressspinner";
import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import FormValidation from "../../../Common/FormValidation";
import Http from "../../../Common/Http";
import IsLogin from "../../../Common/IsLogin";
import { ChartOfAccountStyles } from "../Chart-Of-Account/ChartOfAccountShared";
import CustomerForm from "./CustomerForm";

const emptyInputs = {
  code: "",
  name: "",
  contact_person: "",
  phone: "",
  email: "",
  address: "",
  opening_balance: "",
  credit_limit: "",
  effective_date: "",
  description: "",
  status: "active",
};

const Edit = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [inputs, setInputs] = useState(emptyInputs);
  const [options, setOptions] = useState({ statuses: [] });
  const [error, setError] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);

      try {
        const [customerRes, optionRes] = await Promise.all([
          Http.get("/acc-customer/" + id + "/edit"),
          Http.get("/acc-customer-form-options"),
        ]);

        const data = customerRes.data;

        setInputs({
          code: data.code ?? "",
          name: data.name ?? "",
          contact_person: data.contact_person ?? "",
          phone: data.phone ?? "",
          email: data.email ?? "",
          address: data.address ?? "",
          opening_balance: data.opening_balance ?? "",
          credit_limit: data.credit_limit ?? "",
          effective_date: data.effective_date ?? "",
          description: data.description ?? "",
          status: data.status ?? "active",
        });

        setOptions(optionRes.data);
      } catch (e) {
        console.error("Fetch error:", e);
        setError({
          load: ["Could not load the customer. Please refresh and try again."],
        });
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setInputs((values) => ({ ...values, [name]: value }));
  };

  const validate = () => {
    const errors = {};

    if (!String(inputs.code).trim()) {
      errors.code = ["Code is required."];
    }
    if (!String(inputs.name).trim()) {
      errors.name = ["Name is required."];
    }

    setError(errors);
    return Object.keys(errors).length === 0;
  };

  const formSubmit = () => {
    if (saving || !validate()) {
      return;
    }

    setSaving(true);

    Http.put("/acc-customer/" + id, {
      ...inputs,
      effective_date: inputs.effective_date || null,
      opening_balance:
        inputs.opening_balance === "" ? 0 : inputs.opening_balance,
      credit_limit: inputs.credit_limit === "" ? 0 : inputs.credit_limit,
    })
      .then((res) => {
        navigate("/acc-customer", {
          state: {
            message: res.data.message,
            type: res.data.type || "success",
          },
        });
      })
      .catch((e) => {
        setError(
          e.response?.data?.errors || {
            general: [
              e.response?.data?.message ||
                "Could not save the customer. Please try again.",
            ],
          },
        );
      })
      .finally(() => setSaving(false));
  };

  return (
    <div>
      <IsLogin></IsLogin>
      <div className="am-mainpanel">
        <ChartOfAccountStyles />
        <div className="am-pagetitle">
          <div>
            <h5 className="am-title mb-1">
              Edit Customer{" "}
              {inputs.code && <span className="coa-code">{inputs.code}</span>}
            </h5>
            <p className="text-muted mb-0 tx-12">
              {inputs.name || `Customer #${id}`}
            </p>
          </div>
          <div className="d-flex gap-2">
            <Link
              to={`/view-acc-customer/${id}`}
              className="btn btn-outline-secondary"
            >
              <i className="fa fa-sitemap mg-r-6"></i>Centers &amp; Projects
            </Link>
            <Link to="/acc-customer" className="btn btn-outline-secondary">
              <i className="fa fa-arrow-left mg-r-6"></i>Back to List
            </Link>
          </div>
        </div>

        <div className="am-pagebody">
          <div className="row">
            <div className="col-lg">
              <FormValidation error={error} />
            </div>
          </div>

          {loading ? (
            <div className="coa-panel">
              <div className="coa-empty">
                <ProgressSpinner
                  style={{ width: "42px", height: "42px" }}
                  strokeWidth="6"
                />
              </div>
            </div>
          ) : (
            <CustomerForm
              inputs={inputs}
              options={options}
              onChange={handleChange}
              onSubmit={formSubmit}
              saving={saving}
              submitLabel="Update Customer"
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default Edit;
